export interface ICallableRetryConfig {
    maxAttempts?: number,
    baseDelayMs?: number,
    maxDelayMs?: number,
    retryOn?: (error: unknown, attempt: number) => boolean,
}

// Shared callable retry policy used across mutations so behavior is predictable
// and tuning can happen in one place.
const DEFAULT_MAX_ATTEMPTS = 4;
const DEFAULT_BASE_DELAY_MS = 120;
const DEFAULT_MAX_DELAY_MS = 2000;

const defaultRetryableError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false;

    const firebaseCode = 'code' in error && typeof error.code === 'string'
        ? error.code
        : '';
    const message = 'message' in error && typeof error.message === 'string'
        ? error.message
        : '';

    return (
        firebaseCode === 'functions/aborted'
        || firebaseCode === 'aborted'
        || firebaseCode === 'functions/unavailable'
        || firebaseCode === 'unavailable'
        || firebaseCode === 'functions/deadline-exceeded'
        || firebaseCode === 'deadline-exceeded'
        || message.includes('currently being updated')
    );
};

const wait = (delayMs: number): Promise<void> => new Promise((resolve) => {
    setTimeout(resolve, delayMs);
});

async function runCallableWithRetry<T>(
    operation: () => Promise<T>,
    config?: ICallableRetryConfig,
): Promise<T> {
    const maxAttempts = config?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
    const baseDelayMs = config?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
    const maxDelayMs = config?.maxDelayMs ?? DEFAULT_MAX_DELAY_MS;
    const retryOn = config?.retryOn ?? ((error) => defaultRetryableError(error));

    const executeWithRetry = async (attempt: number): Promise<T> => {
        try {
            return await operation();
        } catch (error) {
            if (
                attempt >= maxAttempts
                || !retryOn(error, attempt)
            ) {
                throw error;
            }

            const jitter = Math.floor(Math.random() * baseDelayMs);
            // Exponential backoff with jitter to reduce stampeding retries.
            const delay = Math.min(baseDelayMs * (2 ** attempt) + jitter, maxDelayMs);
            await wait(delay);
            return executeWithRetry(attempt + 1);
        }
    };

    return executeWithRetry(0);
}

export default runCallableWithRetry;
